import type { PerClassMetric, TestRun } from '@sophia/shared-schemas'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'
import { ArrowRight, Info, Target, TrendingDown, TrendingUp, Minus } from 'lucide-react'
import { Tooltip } from '@/components/ui/Tooltip'
import { PrecisionHint } from './PrecisionHint'
import { FindingCard } from './FindingCard'
import {
  describeGlobalPrecision,
  explainClassPrecision,
  explainGlobalPrecision,
  pickTestedClass,
  type PrecisionExplanation,
} from '../lib/precision-explainer'
import { deriveFindings, diagnoseRun, falseNegatives, falsePositives } from '../lib/run-diagnostics'

interface Props {
  run: TestRun
  previousRun?: TestRun | null
  classFilter: string | null
  onPickClass: (classId: string | null) => void
}

const diagToneClass: Record<'success' | 'warning' | 'danger' | 'neutral', string> = {
  success: 'border-success/30 bg-success-bg',
  warning: 'border-warning/30 bg-warning-bg',
  danger: 'border-danger/30 bg-danger-bg',
  neutral: 'border-border-subtle bg-bg-subtle',
}
const diagDotClass: Record<'success' | 'warning' | 'danger' | 'neutral', string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  neutral: 'bg-neutral-400',
}

function pct(v: number, digits = 1): string {
  if (Number.isNaN(v)) return '—'
  return `${(v * 100).toFixed(digits)}%`
}

function precisionTone(v: number): 'success' | 'warning' | 'danger' {
  if (v >= 0.98) return 'success'
  if (v >= 0.9) return 'warning'
  return 'danger'
}

function KpiCard({
  label,
  value,
  hint,
  tone = 'neutral',
  explanation,
  info,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'success' | 'warning' | 'danger' | 'neutral'
  explanation?: PrecisionExplanation | null
  info?: string
}) {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-fg-1'
  return (
    <Card className="transition-colors duration-200 hover:border-border-strong">
      <CardBody>
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-2xs uppercase tracking-wider text-fg-3">{label}</span>
          {info && (
            <Tooltip content={info} align="start">
              <Info className="h-3 w-3 text-fg-3/60 hover:text-fg-2 transition-colors cursor-help" />
            </Tooltip>
          )}
        </div>
        <div className={cn('font-display font-bold text-3xl data-mono', toneClass)}>{value}</div>
        {hint && <div className="text-xs text-fg-3 mt-1">{hint}</div>}
        <PrecisionHint explanation={explanation ?? null} />
      </CardBody>
    </Card>
  )
}

function ClassBar({
  metric,
  selected,
  onClick,
}: {
  metric: PerClassMetric
  selected: boolean
  onClick: () => void
}) {
  const total = metric.total
  const correct = total > 0 ? metric.correctlyPredicted / total : 0
  const wrong = total > 0 ? metric.misclassified / total : 0
  const notCls = total > 0 ? metric.notClassified / total : 0
  const precisionExplanation = explainClassPrecision({
    total: metric.total,
    predictedCount: metric.predictedCount,
    correctlyPredicted: metric.correctlyPredicted,
  })
  const precisionValue = precisionExplanation?.kind === 'unavailable' ? '—' : pct(metric.precision)
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group w-full text-left rounded-md border p-3 hover:border-border-strong transition-colors',
        selected ? 'border-brand-400 bg-brand-50' : 'border-border-subtle bg-bg-surface',
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-fg-1 truncate">{metric.className}</div>
          <div className="text-xs text-fg-3">
            <span className="data-mono">{metric.total}</span> textos · precisão{' '}
            <span className="data-mono">{precisionValue}</span> · recall{' '}
            <span className="data-mono">{pct(metric.recall)}</span>
          </div>
          <PrecisionHint explanation={precisionExplanation} />
        </div>
        <Badge tone={precisionTone(metric.f1)}>
          <span className="data-mono">F1 {pct(metric.f1)}</span>
        </Badge>
      </div>
      <div className="h-2 rounded-pill bg-neutral-100 overflow-hidden flex">
        <div className="bg-success" style={{ width: `${correct * 100}%` }} />
        <div className="bg-danger" style={{ width: `${wrong * 100}%` }} />
        <div className="bg-warning" style={{ width: `${notCls * 100}%` }} />
      </div>
      <div className="flex items-center gap-3 text-2xs text-fg-3 mt-1.5">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-success" /> acertou {metric.correctlyPredicted}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-danger" /> errou {metric.misclassified}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-warning" /> não classif. {metric.notClassified}
        </span>
      </div>
    </button>
  )
}

function DeltaTile({ label, from, to }: { label: string; from: number; to: number }) {
  const delta = to - from
  const pp = Math.abs(delta * 100).toFixed(1)
  const tone = delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-fg-3'
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus
  return (
    <div className="rounded-md border border-border-subtle px-4 py-3">
      <div className="text-2xs uppercase tracking-wider text-fg-3 mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="data-mono text-sm text-fg-3">{pct(from)}</span>
        <ArrowRight className="h-3 w-3 text-fg-3" />
        <span className="data-mono text-lg font-bold text-fg-1">{pct(to)}</span>
      </div>
      <div className={cn('inline-flex items-center gap-1 data-mono text-xs font-semibold mt-1', tone)}>
        <Icon className="h-3.5 w-3.5" />
        {delta === 0 ? 'sem mudança' : `${delta > 0 ? '+' : '−'}${pp} p.p.`}
      </div>
    </div>
  )
}

export function OverviewTab({ run, previousRun, classFilter, onPickClass }: Props) {
  const metrics = run.metrics
  if (!metrics) return <p className="text-sm text-fg-3">Sem métricas.</p>

  const visiblePerClass = classFilter
    ? metrics.perClass.filter((p) => p.classId === classFilter)
    : metrics.perClass

  const globalPrecisionExplanation = explainGlobalPrecision({
    classified: metrics.classified,
    classifiedCorrect: metrics.classifiedCorrect,
    totalRows: metrics.totalRows,
  })
  const globalPrecisionDescription = describeGlobalPrecision({
    classified: metrics.classified,
    classifiedCorrect: metrics.classifiedCorrect,
  })
  // Se filtrado por classe, o destaque é a própria classe filtrada; senão, a dominante.
  const testedClass = pickTestedClass(
    classFilter ? visiblePerClass : metrics.perClass,
  )

  const diagnosis = diagnoseRun(metrics)
  const findings = deriveFindings(metrics)
  const prev = previousRun?.metrics ?? null

  return (
    <div className="flex flex-col gap-5">
      {/* Diagnóstico executivo da run */}
      <div className={cn('flex items-start gap-3 rounded-lg border px-4 py-3.5', diagToneClass[diagnosis.tone])}>
        <span className={cn('mt-1.5 h-2 w-2 rounded-full shrink-0', diagDotClass[diagnosis.tone])} />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-fg-1">{diagnosis.headline}</div>
          <p className="text-sm text-fg-2 mt-0.5">{diagnosis.detail}</p>
        </div>
      </div>

      {testedClass && (
        <div className="flex items-center gap-3 rounded-md border border-brand-100 bg-brand-50 px-4 py-3">
          <Target className="h-5 w-5 shrink-0 text-brand-700" />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-brand-700 font-semibold">Classe testada</div>
            <div className="text-sm text-fg-1 mt-0.5">
              <span className="font-semibold">{testedClass.className}</span> teve{' '}
              <span className={cn('data-mono font-bold', precisionTone(testedClass.accuracy) === 'success' ? 'text-success' : precisionTone(testedClass.accuracy) === 'warning' ? 'text-warning' : 'text-danger')}>
                {pct(testedClass.accuracy)}
              </span>{' '}
              de acerto — {testedClass.hits} de {testedClass.total} textos identificados corretamente.
            </div>
          </div>
        </div>
      )}

      {/* Cards executivos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total de linhas" value={String(metrics.totalRows)} />
        <KpiCard
          label="Classificadas"
          value={String(metrics.classified)}
          hint={pct(metrics.coverageGlobal)}
          info="Linhas que alguma regra conseguiu classificar."
        />
        <KpiCard
          label="Não classificadas"
          value={String(metrics.notClassified)}
          hint={`${pct(metrics.llmRate)} vai pro LLM`}
          tone={metrics.llmRate >= 0.5 ? 'warning' : 'neutral'}
          info="Linhas que nenhuma regra capturou e seguem pro LLM. Custo recorrente do pipeline."
        />
        <KpiCard
          label="Conflitos"
          value={String(metrics.conflictRows)}
          hint={pct(metrics.conflictRate)}
          info="Linhas onde regras de 2+ classes casaram ao mesmo tempo. Regras competindo."
        />
        <KpiCard
          label="Precisão"
          value={globalPrecisionExplanation?.kind === 'unavailable' ? '—' : pct(metrics.precisionGlobal, 2)}
          hint={`${metrics.classifiedCorrect}/${metrics.classified} corretas`}
          tone={precisionTone(metrics.precisionGlobal)}
          explanation={globalPrecisionExplanation}
          info="Das linhas que as regras classificaram, quantas foram pra classe certa. Linhas que foram pro LLM não entram nesta conta."
        />
        <KpiCard
          label="Cobertura"
          value={pct(metrics.coverageGlobal)}
          hint={`${metrics.classified} de ${metrics.totalRows}`}
          info="Quanto da base as regras classificaram sozinhas. Maior cobertura = pipeline mais barato."
        />
        <KpiCard
          label="Falsos positivos"
          value={String(falsePositives(metrics))}
          tone={falsePositives(metrics) > 0 ? 'danger' : 'neutral'}
          info="Linhas que as regras classificaram numa classe errada."
        />
        <KpiCard
          label="Falsos negativos"
          value={String(falseNegatives(metrics))}
          tone={falseNegatives(metrics) > 0 ? 'warning' : 'neutral'}
          info="Linhas de classes cadastradas que não foram corretamente identificadas (classe errada ou não classificadas)."
        />
      </div>

      <p className="text-sm text-fg-3 -mt-1">
        <span className="font-medium text-fg-2">Precisão:</span> {globalPrecisionDescription} Linhas que foram pro LLM
        (não classificadas pelas regras) não entram nessa conta.
      </p>

      {/* Comparação com run anterior */}
      <Card>
        <CardHeader>
          <h3 className="font-display font-bold text-base text-fg-1">Comparação com run anterior</h3>
        </CardHeader>
        <CardBody>
          {prev ? (
            <div className="grid grid-cols-2 gap-3">
              <DeltaTile
                label="Precisão"
                from={prev.precisionGlobal}
                to={metrics.precisionGlobal}
              />
              <DeltaTile
                label="Cobertura"
                from={prev.coverageGlobal}
                to={metrics.coverageGlobal}
              />
            </div>
          ) : (
            <p className="text-sm text-fg-3 italic">
              Esta é a primeira run desta base. Rode novamente após ajustar as regras pra comparar o antes/depois — ou
              veja o histórico completo na aba Comparação.
            </p>
          )}
        </CardBody>
      </Card>

      {/* Top problemas */}
      {findings.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-display font-bold text-base text-fg-1">Top problemas</h3>
            <p className="text-sm text-fg-3 mt-0.5">Ordenados por impacto. Resolva de cima pra baixo.</p>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {findings.map((f) => (
                <FindingCard key={f.id} finding={f} />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-display font-bold text-base text-fg-1">Por classe</h3>
              <p className="text-sm text-fg-3 mt-0.5">
                Clique numa classe pra filtrar todas as tabs. {classFilter && '(filtro ativo)'}
              </p>
            </div>
            {classFilter && (
              <button
                type="button"
                onClick={() => onPickClass(null)}
                className="text-xs text-brand-700 hover:text-brand-800 underline"
              >
                Limpar filtro
              </button>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <div className="flex flex-col gap-2">
            {visiblePerClass.map((m) => (
              <ClassBar
                key={m.classId ?? '__null__'}
                metric={m}
                selected={classFilter === m.classId}
                onClick={() => onPickClass(classFilter === m.classId ? null : m.classId)}
              />
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
